import { createApi } from '@reduxjs/toolkit/query/react'
import baseQueryWithReauth from './baseQueryWithReauth';
import { convertDateToUTCISO } from '../components/date-formatters';

const type = "user";

// Define a service using a base URL and expected endpoints
export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: baseQueryWithReauth,
  refetchOnMountOrArgChange: 0,
  keepUnusedDataFor: 0,
  tagTypes: [type],
  endpoints: (builder) => ({
    getUser: builder.query<any, { userId: string }>({      
      query: (arg) => {
        const { userId } = arg;
        return `user/${userId}`
      },
      providesTags: (returnValue, args) => [{ type: type , id: 'LIST' }, ...(returnValue?.data || []).map((user: any) => ({ type: type, id: user.id }))]      
    }),  
    getUserRoles: builder.query<any, { userId: string }>({      
      query: (arg) => {
        const { userId } = arg;
        return `user/${userId}/roles`
      }            
    }),  
    getUserEmailAddress: builder.query<any, { userId: string }>({      
      query: (arg) => {
        const { userId } = arg;
        return `user/${userId}/emailAddress`
      },
      providesTags: (returnValue, args) => [{ type: type , id: 'LIST' }, ...(returnValue?.data || []).map((user: any) => ({ type: type, id: user.id }))]      
    }),  
    getUsers: builder.query<any, void>({
      query: () => { return `user/list` }      
    }),    
    getUserClients: builder.query<any, void>({      
      query: (arg) => {    
        return `client/user`
      }
    }),      
    getLogins: builder.query<any, { fromDate: any, toDate: any}>({
      query: (arg) => {       
        const { fromDate, toDate } = arg;           
        return `user/logins?fromDate=${convertDateToUTCISO(fromDate)}&toDate=${convertDateToUTCISO(toDate)}` },
      providesTags: (returnValue, args) => { return [{ type: type , id: 'LIST' }, ...(returnValue?.userLogins || [])]}
    }),    
    add: builder.mutation<any, any>({
      query: (formData: any) => {        
        return {
        url: `user`,
        method: 'POST',
        body: formData,
      }},
      invalidatesTags: [type],
    }),  
    update: builder.mutation<any, any>({
      query: (formData: any) => {   
        return {
        url: `user/${formData.userId}`,
        method: 'PUT',
        body: formData,
      }},
      invalidatesTags: [type],
    }),
    updateEmailAdress: builder.mutation<any, any>({
      query: (formData: any) => {   
        return {
        url: `user/${formData.userId}/emailAddress`,
        method: 'PUT',
        body: formData,
      }},
      invalidatesTags: [type],
    }),
    updateRoles: builder.mutation<any, any>({
      query: (formData: any) => {   
        return {
        url: `user/${formData.userId}/roles`,
        method: 'PUT',
        body: formData,
      }},
      invalidatesTags: [type],
    }),
    removeFromClient: builder.mutation<any, any>({
      query: (formData: any) => {
        const { userId } = formData;
        return {
        url: `user/${userId}/removeFromClient`,
        method: 'PUT'
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    removeMultipleFromClient: builder.mutation<any, any>({
      query: (formData: any) => {        
        return {
        url: `user/removeMultipleFromClient`,
        method: 'PUT',
        body: formData
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    delete: builder.mutation<any, any>({
      query: (formData: any) => {
        const { userId } = formData;
        return {
        url: `user/${userId}`,
        method: 'DELETE'
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    deleteMultiple: builder.mutation<any, any>({
      query: (formData: any) => {
        return {
        url: `user`,
        method: 'DELETE',
        body: formData
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    resetTwoFactor: builder.mutation<any, any>({
      query: (formData: any) => {
        const { userId } = formData;
        return {
        url: `user/${userId}/ResetTwoFactor`,
        method: 'PUT'
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
    updateDarkMode: builder.mutation<any, { useDarkMode: boolean }>({
      query: (formData: any) => {
        return {
        url: `user/darkMode`,
        method: 'PUT',
        body: formData
      }},
      invalidatesTags: [{ type: type, id: 'LIST' }]
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetUserQuery, 
              useGetUserEmailAddressQuery,
               useGetUserRolesQuery,
               useGetUsersQuery,
               useGetUserClientsQuery,            
               useAddMutation, 
               useUpdateMutation, 
               useUpdateEmailAdressMutation,
               useUpdateRolesMutation,
               useDeleteMutation,
               useDeleteMultipleMutation,
               useRemoveFromClientMutation,
               useRemoveMultipleFromClientMutation,
               useResetTwoFactorMutation,
               useUpdateDarkModeMutation,
               useGetLoginsQuery
             } = userApi;